import { Hub } from "aws-amplify/utils";
import {
  signIn as ampSignIn,
  signOut as ampSignOut,
  confirmSignIn as ampConfirmSignIn,
  resetPassword as ampResetPassword,
  confirmResetPassword as ampConfirmResetPassword,
  fetchAuthSession,
  getCurrentUser,
} from "aws-amplify/auth";
import type { User } from "@observe-tech/mfe-shell-api";
import { clearSyncedTokens } from "./syncedTokens";

export interface SignInResult {
  isSignedIn: boolean;
  nextStep?: { signInStep: string };
}

export interface ResetPasswordResult {
  isPasswordReset: boolean;
  nextStep?: { resetPasswordStep: string };
}

export interface AuthApi {
  getToken(opts?: { forceRefresh?: boolean }): Promise<string>;
  signIn(username: string, password: string): Promise<SignInResult>;
  confirmSignIn(newPassword: string): Promise<{ isSignedIn: boolean }>;
  resetPassword(username: string): Promise<ResetPasswordResult>;
  confirmResetPassword(
    username: string,
    confirmationCode: string,
    newPassword: string,
  ): Promise<void>;
  signOut(): Promise<void>;
  loadCurrentUser(): Promise<User>;
  onTokenRefresh(cb: (token: string) => void): () => void;
  onSessionExpired(cb: () => void): () => void;
  startTokenWatcher(intervalMs?: number): () => void;
  stopTokenWatcher(): void;
}

const REFRESH_LEAD_MS = 5 * 60 * 1000;

let cached: AuthApi | null = null;

export function createAuthApi(): AuthApi {
  if (cached) return cached;

  const tokenSubs = new Set<(t: string) => void>();
  const expiredSubs = new Set<() => void>();

  function emitToken(t: string) {
    for (const s of tokenSubs) {
      try {
        s(t);
      } catch {
        /* swallow subscriber errors */
      }
    }
  }

  function emitExpired() {
    for (const s of expiredSubs) {
      try {
        s();
      } catch {
        /* swallow */
      }
    }
  }

  Hub.listen("auth", ({ payload }) => {
    switch (payload.event) {
      case "tokenRefresh":
        void fetchAuthSession()
          .then((s) => {
            const t = s.tokens?.idToken?.toString();
            if (t) emitToken(t);
            else emitExpired();
          })
          .catch(() => emitExpired());
        break;
      case "tokenRefresh_failure":
      case "signedOut":
        emitExpired();
        break;
      case "signedIn":
        void fetchAuthSession()
          .then((s) => {
            const t = s.tokens?.idToken?.toString();
            if (t) emitToken(t);
          })
          .catch(() => {});
        break;
    }
  });

  let watcher: ReturnType<typeof setInterval> | null = null;

  const api: AuthApi = {
    async getToken({ forceRefresh = false } = {}) {
      const session = await fetchAuthSession({ forceRefresh });
      const t = session.tokens?.idToken;
      if (!t) throw new Error("No session");
      return t.toString();
    },
    async signIn(username, password) {
      clearSyncedTokens();
      // Drop any lingering Cognito session so a stale/partial sign-in (e.g. a
      // previous user that was not fully signed out) does not block this one.
      try {
        await ampSignOut();
      } catch {
        /* no existing session */
      }
      const r = await ampSignIn({ username, password });
      return { isSignedIn: r.isSignedIn, nextStep: r.nextStep };
    },
    async confirmSignIn(newPassword) {
      const r = await ampConfirmSignIn({ challengeResponse: newPassword });
      return { isSignedIn: r.isSignedIn };
    },
    async resetPassword(username) {
      const r = await ampResetPassword({ username });
      return { isPasswordReset: r.isPasswordReset, nextStep: r.nextStep };
    },
    async confirmResetPassword(username, confirmationCode, newPassword) {
      await ampConfirmResetPassword({
        username,
        confirmationCode,
        newPassword,
      });
    },
    async signOut() {
      try {
        await ampSignOut();
      } finally {
        clearSyncedTokens();
        emitExpired();
      }
    },
    async loadCurrentUser() {
      const u = await getCurrentUser();
      const email = u.signInDetails?.loginId ?? "";
      return {
        id: u.userId,
        cognitoSub: u.userId,
        email,
        name: email,
      };
    },
    onTokenRefresh(cb) {
      tokenSubs.add(cb);
      return () => {
        tokenSubs.delete(cb);
      };
    },
    onSessionExpired(cb) {
      expiredSubs.add(cb);
      return () => {
        expiredSubs.delete(cb);
      };
    },
    startTokenWatcher(intervalMs = 60_000) {
      if (watcher) clearInterval(watcher);
      const tick = async () => {
        try {
          const session = await fetchAuthSession();
          const id = session.tokens?.idToken;
          if (!id) {
            emitExpired();
            return;
          }
          const exp = (id.payload as { exp?: number } | undefined)?.exp;
          if (typeof exp === "number") {
            const msToExpiry = exp * 1000 - Date.now();
            if (msToExpiry < REFRESH_LEAD_MS) {
              const refreshed = await fetchAuthSession({ forceRefresh: true });
              const newT = refreshed.tokens?.idToken?.toString();
              if (newT) emitToken(newT);
              else emitExpired();
            }
          }
        } catch {
          emitExpired();
        }
      };
      void tick();
      watcher = setInterval(() => void tick(), intervalMs);
      return () => api.stopTokenWatcher();
    },
    stopTokenWatcher() {
      if (watcher) clearInterval(watcher);
      watcher = null;
    },
  };

  cached = api;
  return api;
}
